import React, { useState } from "react";
import PropTypes from "prop-types";
import { Button, Label, Spinner } from "reactstrap";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import CustomInput from "@/shared/components/form/CustomInput";
import { AsyncPaginate } from "react-select-async-paginate";
import { useAddMutation, useUpdateMutation } from "@/services/pcApi";
import { APPVERSION_TAG } from "@/shared/tagFile";
import { successToast } from "@/shared/components/toast";

const AppVersionForm = ({
  editData,
  setIsAddModalOpen,
}) => {

  const [addAppVersion, { isLoading: isAppVersionAddLoading }] =
    useAddMutation();
  const [updateAppVersion, { isLoading: isAppVersionUpdateLoading }] =
    useUpdateMutation();
  const [editRecordDetail, setEditRecordDetail] = useState(
    editData ? editData : null
  );

  const isOption = [
    {
      value: true,
      label: "Yes",
    },
    {
      value: false,
      label: "No",
    }
  ]

  const isPlatformOption = [
    {
      value: "ios",
      label: "IOS",
    },
    {
      value: "android",
      label: "Android",
    }
  ]
  const appVersionValidationSchema = Yup.object().shape({
    AppName: Yup.string().required("Please enter App Name"),
    UpdateDescription: Yup.string().required("Please enter UpdateDescription"),
    Platform: Yup.mixed().required(("Please select any platform.")),
    IsMandatoryUpdate: Yup.mixed().required("Please enter IsMandatoryUpdate"),
    VersionNumber: Yup.string().required("Please enter VersionNumber"),
    SessionTimeout: Yup.number()
      .required('Please enter a session timeout')
      .min(5, 'Session timeout must be at least 5 minutes')
  });

  const onSubmit = (values) => {
    const appDetail = {
      AppName: values.AppName,
      Platform: values.Platform.value,
      UpdateDescription: values.UpdateDescription,
      IsMandatoryUpdate: values.IsMandatoryUpdate.value,
      VersionNumber: values.VersionNumber,
      SessionTimeout: values.SessionTimeout,
      IsDeployed: values.IsDeployed.value,
    };

    if (editRecordDetail && Object.entries(editRecordDetail).length > 0) {
      appDetail.Id = editRecordDetail.Id;
      updateAppVersion({
        entity: "CMSReference/AppVersion",
        data: appDetail,
        tag: APPVERSION_TAG,
      }).then((response) => {
        if (!response.error) {
          successToast("AppVersion updated successfully.");
          setIsAddModalOpen(false)
        }
      });
    } else {
      addAppVersion({
        entity: "CMSReference/AppVersion",
        data: appDetail,
        tag: APPVERSION_TAG,
      }).then((response) => {
        if (!response.error) {
          successToast("AppVersion added successfully.");
          setIsAddModalOpen(false)
        }
      });
    }
  };

  return (
    <Formik
      initialValues={{
        AppName: "TableTop Fanatics",
        Platform: editRecordDetail && editRecordDetail.Platform ? {
          label: editRecordDetail?.Platform === "ios" ? "IOS" : "Android",
          value: editRecordDetail?.Platform,
        } : "",
        UpdateDescription: editRecordDetail && editRecordDetail.UpdateDescription ? editRecordDetail.UpdateDescription : "Click on Update to get the latest version",
        IsMandatoryUpdate: editRecordDetail ? editRecordDetail.IsMandatoryUpdate ? isOption[0] : isOption[1] : "",
        IsDeployed: editRecordDetail ? editRecordDetail.IsDeployed ? isOption[0] : isOption[1] : "",
        SessionTimeout: editRecordDetail && editRecordDetail.SessionTimeout ? editRecordDetail.SessionTimeout : "",
        VersionNumber: editRecordDetail && editRecordDetail.VersionNumber ? editRecordDetail.VersionNumber : "",
      }}
      onSubmit={onSubmit}
      validationSchema={appVersionValidationSchema}
    >
      {(formik) => {
        const {
          errors,
          touched,
          values,
          handleSubmit,
          handleBlur,
          handleChange,
          setFieldValue,
        } = formik;

        return (
          <Form>
            <CustomInput
              label="App Name"
              name="AppName"
              fieldErrors={errors.AppName}
              fieldTouched={touched.AppName}
              fieldValue={values.AppName}
              handleBlur={handleBlur}
              handleChange={handleChange}
              readonly
              required
            />
            <CustomInput
              label="UpdateDescription"
              name="UpdateDescription"
              type="textarea"
              fieldErrors={errors.UpdateDescription}
              fieldTouched={touched.UpdateDescription}
              fieldValue={values.UpdateDescription}
              handleBlur={handleBlur}
              handleChange={handleChange}
              required
            />
            <CustomInput
              label="Version"
              name="VersionNumber"
              fieldErrors={errors.VersionNumber}
              fieldTouched={touched.VersionNumber}
              fieldValue={values.VersionNumber}
              handleBlur={handleBlur}
              handleChange={handleChange}
              required
            />
            <div className="mb-3">
              <Label for="IsMandatoryUpdate">
                IsMandatoryUpdate
              </Label>
              <AsyncPaginate
                className={`lazySelect`}
                name="IsMandatoryUpdate"
                value={values.IsMandatoryUpdate}
                options={isOption}
                placeholder={`Select IsMandatoryUpdate`}
                onBlur={handleBlur}
                onChange={(e) => setFieldValue("IsMandatoryUpdate", e)}
              />
            </div>
            <div className="mb-3">
              <Label for="Platform">
                Platform
              </Label>
              <AsyncPaginate
                className={`lazySelect`}
                name="Platform"
                value={values.Platform}
                options={isPlatformOption}
                placeholder={`Select Platform`}
                onBlur={handleBlur}
                onChange={(e) => setFieldValue("Platform", e)}
                required
              />
              {touched.Platform && errors.Platform && (
                <font color="red">{errors.Platform}</font>
              )}
            </div>
            <CustomInput
              label="SessionTimeout(Min)"
              name="SessionTimeout"
              type="number"
              fieldErrors={errors.SessionTimeout}
              fieldTouched={touched.SessionTimeout}
              fieldValue={values.SessionTimeout}
              handleBlur={handleBlur}
              handleChange={handleChange}
              required
            />
            <div className="mb-3">
              <Label for="IsDeployed">
                IsDeployed
              </Label>
              <AsyncPaginate
                className={`lazySelect`}
                name="IsDeployed"
                value={values.IsDeployed}
                options={isOption}
                placeholder={`Select IsDeployed`}
                onBlur={handleBlur}
                onChange={(e) => setFieldValue("IsDeployed", e)}
                required
              />
            </div>
            <Button
              disabled={isAppVersionAddLoading || isAppVersionUpdateLoading}
              onClick={handleSubmit}
              className="modal_ok float-right"
              color="success"
            >
              {(isAppVersionAddLoading ||
                isAppVersionUpdateLoading) && (
                  <Spinner size="sm" variant="light" />
                )}{" "}
              Save
            </Button>
          </Form>
        );
      }}
    </Formik>
  );
};

AppVersionForm.propTypes = {
  editData: PropTypes.object,
  setActiveTab: PropTypes.func,
  parentId: PropTypes.number,
  setIsAddModalOpen: PropTypes.func,
  isAddModalOpen: PropTypes.object || PropTypes.bool,
  setCurrentAppVersionId: PropTypes.func,
};

export default AppVersionForm;
